import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { supabase } from "../../config/supabaseClient";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Viewer IANA timezone used for Website Analytics day buckets. */
export function getAnalyticsTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Local calendar YYYY-MM-DD in the viewer timezone. */
export function analyticsLocalDate(
  daysOffset = 0,
  timeZone: string = getAnalyticsTimeZone(),
): string {
  return dayjs().tz(timeZone).add(daysOffset, "day").format("YYYY-MM-DD");
}

/**
 * Inclusive local-day range → ISO bounds for timestamptz filters
 * (e.g. direct table queries that do not go through day-bucket RPCs).
 */
export function analyticsLocalDayIsoRange(
  startDate: string,
  endDate: string,
  timeZone: string = getAnalyticsTimeZone(),
): { startIso: string; endIso: string } {
  return {
    startIso: dayjs.tz(`${startDate}T00:00:00`, timeZone).toISOString(),
    endIso: dayjs.tz(`${endDate}T23:59:59.999`, timeZone).toISOString(),
  };
}

/**
 * UTC calendar dates that overlap the viewer's local [start, end] window.
 * Existing template RPCs bucket by UTC day; widening the query range prevents
 * evening "today" events from being clipped without changing any SQL.
 */
export function analyticsUtcOverlapRange(
  startDate: string,
  endDate: string,
  timeZone: string = getAnalyticsTimeZone(),
): { startDate: string; endDate: string } {
  return {
    startDate: dayjs
      .tz(`${startDate}T00:00:00`, timeZone)
      .utc()
      .format("YYYY-MM-DD"),
    endDate: dayjs
      .tz(`${endDate}T23:59:59.999`, timeZone)
      .utc()
      .format("YYYY-MM-DD"),
  };
}

/**
 * Parse a YYYY-MM-DD (or date-like) value as a local calendar day for chart
 * axis labels — avoids `new Date("YYYY-MM-DD")` UTC-midnight shifts.
 */
export function parseAnalyticsAxisDate(value: unknown): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const raw = String(value ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

/** Chronological compare for chart axis labels like "Oct 04" / ISO dates / Date objects. */
export function compareAnalyticsFormattedDates(a: unknown, b: unknown): number {
  return parseAnalyticsAxisDate(a).getTime() - parseAnalyticsAxisDate(b).getTime();
}

/**
 * Call existing template analytics RPCs with their current signatures.
 * Widens start/end to UTC days that overlap the viewer's local range so
 * today's events are included — no DB migration required.
 */
export async function rpcTemplateAnalytics(
  fn: string,
  params: Record<string, unknown>,
) {
  const { viewer_tz: _ignored, ...rpcParams } = params;
  const timeZone = getAnalyticsTimeZone();
  const start = rpcParams.start_date;
  const end = rpcParams.end_date;
  if (typeof start === "string" && typeof end === "string") {
    const overlap = analyticsUtcOverlapRange(start, end, timeZone);
    rpcParams.start_date = overlap.startDate;
    rpcParams.end_date = overlap.endDate;
  }
  return supabase.rpc(fn, rpcParams);
}
